import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, Sparkles, ChevronRight, Plus, Share2 } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import PublicPageHeader from '@components/common/PublicPageHeader';
import { kundliService } from '@services/kundliService';
import { useAuthStore } from '@store/authStore';
import { useSettingsStore } from '@store/settingsStore';
import { useKundliStore } from '@store/kundliStore';
import toast from 'react-hot-toast';
import { SEOHead } from '@/seo';
import { FREE_COMPATIBILITY_TRANSLATIONS, translateRashi } from '@/languages/freeCompatibility';
import { getLocalISODate, parseKundliDob } from '@utils/dateUtils';

import CompatibilityForm from './components/CompatibilityForm';
import CompatibilityResults from './components/CompatibilityResults';
import ShareCard from './components/ShareCard';

const FreeCompatibility = () => {
    const { lang: urlLang } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { contentLanguage, setContentLanguage } = useSettingsStore();

    const getDefaultFormData = () => {
        return {
            name: '',
            dob: getLocalISODate(),
            birth_time: '12:00',
            location: '',
            latitude: 28.6139,
            longitude: 77.2090,
            gender: 'Male'
        };
    };

    // Zustand store for persistence
    const {
        compatibilityResults,
        compatibilityPerson1,
        compatibilityPerson2,
        compatibilityPayloads,
        setCompatibilityResults,
        setCompatibilityPerson1,
        setCompatibilityPerson2,
        setCompatibilityPayloads,
        clearCompatibilitySession,
    } = useKundliStore();

    // Initialize state from Zustand store or defaults
    const [person1, setPerson1] = useState(() =>
        compatibilityPerson1 || getDefaultFormData()
    );
    const [person2, setPerson2] = useState(() =>
        compatibilityPerson2 || { ...getDefaultFormData(), gender: 'Female' }
    );
    const [selectedKundli1, setSelectedKundli1] = useState<any>(null);
    const [selectedKundli2, setSelectedKundli2] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<any>(compatibilityResults);
    const [showShareModal, setShowShareModal] = useState(false);

    // Store last used payloads to re-fetch on language change
    const lastPayloadsRef = useRef<{ payload1: any; payload2: any } | null>(compatibilityPayloads);
    const prevLanguageRef = useRef<string | null>(null);

    // Sync results with Zustand store
    useEffect(() => {
        if (results) {
            setCompatibilityResults(results);
        }
    }, [results, setCompatibilityResults]);

    // Sync person data with Zustand store
    useEffect(() => {
        if (person1.name) {
            setCompatibilityPerson1(person1);
        }
    }, [person1, setCompatibilityPerson1]);

    useEffect(() => {
        if (person2.name) {
            setCompatibilityPerson2(person2);
        }
    }, [person2, setCompatibilityPerson2]);

    // Language state - initialized from URL param, user preference, or Zustand store
    const [language, setLanguage] = useState<'english' | 'hindi'>(() => {
        if (urlLang === 'hindi' || urlLang === 'english') {
            return urlLang as 'english' | 'hindi';
        }
        if (user?.content_language) {
            const lang = user.content_language.toLowerCase();
            return lang === 'hindi' ? 'hindi' : 'english';
        }
        return contentLanguage;
    });

    // Listen to Zustand store changes for language
    useEffect(() => {
        let storeLang: 'english' | 'hindi' = contentLanguage;

        if (user?.content_language) {
            const userLang = user.content_language.toLowerCase();
            storeLang = userLang === 'hindi' ? 'hindi' : 'english';
        }

        if (storeLang !== language) {
            setLanguage(storeLang);
        }
    }, [user?.content_language, contentLanguage]);

    // Update state when URL param changes
    useEffect(() => {
        if (urlLang && (urlLang === 'hindi' || urlLang === 'english') && urlLang !== language) {
            setLanguage(urlLang as 'english' | 'hindi');
        }
    }, [urlLang]);

    // Update URL when language changes
    useEffect(() => {
        navigate(`/kundli-matching/${language}`, { replace: true });
        setContentLanguage(language);
    }, [language, navigate, setContentLanguage]);

    // Re-fetch compatibility data when language changes (if payloads exist)
    useEffect(() => {
        const refetchOnLanguageChange = async () => {
            // Skip on initial mount or if no payloads exist (means no analysis done yet)
            if (!prevLanguageRef.current || !lastPayloadsRef.current) {
                prevLanguageRef.current = language;
                return;
            }

            // Only re-fetch if language actually changed
            if (prevLanguageRef.current !== language) {
                prevLanguageRef.current = language;
                setLoading(true);
                try {
                    const { payload1, payload2 } = lastPayloadsRef.current;
                    const response = await kundliService.generateCompatibilityAnalysis(payload1, payload2, language);
                    setResults(response);
                } catch (error) {
                    console.error('Error re-fetching compatibility:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        refetchOnLanguageChange();
    }, [language]); // Only depend on language, use ref to check if payloads exist

    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_COMPATIBILITY_TRANSLATIONS[langKey];

    // Get compatibility level for share card
    const getCompatibilityLevel = () => {
        if (!results) return { label: '', percentage: 0 };
        const gunaMilan = results.compatibility_data?.guna_milan;
        const totalScore = gunaMilan?.total || 0;
        const maxScore = gunaMilan?.max_total || 36;
        const percentage = parseFloat(((totalScore / maxScore) * 100).toFixed(1));

        if (percentage >= 80) return { label: t.excellent, percentage };
        if (percentage >= 60) return { label: t.good, percentage };
        if (percentage >= 40) return { label: t.average, percentage };
        if (percentage >= 20) return { label: t.belowAverage, percentage };
        return { label: t.low, percentage };
    };

    const handleGenerate = async () => {
        if (!person1.name || !person1.dob || !person1.location) {
            toast.error(t.fillPerson1);
            return;
        }
        if (!person2.name || !person2.dob || !person2.location) {
            toast.error(t.fillPerson2);
            return;
        }

        setLoading(true);
        try {
            const parsedDate1 = parseKundliDob(person1.dob);
            const parsedDate2 = parseKundliDob(person2.dob);

            if (!parsedDate1 || !parsedDate2) {
                toast.error(t.invalidDate);
                setLoading(false);
                return;
            }

            const timeParts1 = person1.birth_time.split(':');
            const timeParts2 = person2.birth_time.split(':');

            const payload1 = {
                name: person1.name,
                gender: person1.gender,
                year: parsedDate1.year,
                month: parsedDate1.month,
                day: parsedDate1.day,
                hour: parseInt(timeParts1[0]),
                minute: parseInt(timeParts1[1]),
                location: person1.location,
                latitude: person1.latitude,
                longitude: person1.longitude
            };

            const payload2 = {
                name: person2.name,
                gender: person2.gender,
                year: parsedDate2.year,
                month: parsedDate2.month,
                day: parsedDate2.day,
                hour: parseInt(timeParts2[0]),
                minute: parseInt(timeParts2[1]),
                location: person2.location,
                latitude: person2.latitude,
                longitude: person2.longitude
            };

            const response = await kundliService.generateCompatibilityAnalysis(payload1, payload2, language);
            setResults(response);
            // Store payloads for re-fetching on language change
            lastPayloadsRef.current = { payload1, payload2 };
            setCompatibilityPayloads({ payload1, payload2 });
            toast.success(t.analysisGenerated);

            setTimeout(() => {
                document.getElementById('compatibility-results')?.scrollIntoView({ behavior: 'smooth' });
            }, 100);

        } catch (error) {
            console.error('Error generating compatibility:', error);
            toast.error(t.analysisFailed);
        } finally {
            setLoading(false);
        }
    };

    const handleNewAnalysis = () => {
        // Clear local state
        setResults(null);
        lastPayloadsRef.current = null;
        setPerson1(getDefaultFormData());
        setPerson2({ ...getDefaultFormData(), gender: 'Female' });
        setSelectedKundli1(null);
        setSelectedKundli2(null);

        // Clear Zustand store
        clearCompatibilitySession();

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-cosmic-950 text-white font-sans selection:bg-primary-900 selection:text-white relative pb-16 md:pb-0">
            <SEOHead
                title={t.title}
                description={t.description}
            />

            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-12 md:pb-24 px-3 sm:px-6">
                <div className={`container mx-auto ${results ? 'max-w-5xl' : 'max-w-4xl'}`}>

                    <PublicPageHeader
                        title={t.title}
                        highlightedText={t.title}
                        description={t.description}
                        icon={Heart}
                    />

                    {!results && (
                        <CompatibilityForm
                            person1={person1}
                            setPerson1={setPerson1}
                            person2={person2}
                            setPerson2={setPerson2}
                            handleGenerate={handleGenerate}
                            loading={loading}
                            language={language}
                            selectedKundli1={selectedKundli1}
                            setSelectedKundli1={setSelectedKundli1}
                            selectedKundli2={selectedKundli2}
                            setSelectedKundli2={setSelectedKundli2}
                            translations={t}
                        />
                    )}

                    {results && (
                        <div id="compatibility-results" className="space-y-6 md:space-y-12">
                            {/* Mobile: Full width 50-50 split, Desktop: Right aligned */}
                            <div className="grid grid-cols-2 gap-2 md:flex md:justify-end md:gap-3 mb-4">
                                <button
                                    onClick={() => setShowShareModal(true)}
                                    className="px-4 md:px-6 py-3 rounded-xl md:rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap bg-white/10 border border-white/20 text-white hover:bg-white/20"
                                >
                                    <Share2 size={18} className="md:w-5 md:h-5" />
                                    {t.share}
                                </button>
                                <button
                                    onClick={handleNewAnalysis}
                                    className="px-4 md:px-6 py-3 rounded-xl md:rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-2 whitespace-nowrap bg-gradient-to-r from-primary-600 to-secondary-600 text-white hover:from-primary-700 hover:to-secondary-700 shadow-lg shadow-primary-900/40"
                                >
                                    <Plus size={18} className="md:w-5 md:h-5" />
                                    {t.newAnalysis}
                                </button>
                            </div>

                            <CompatibilityResults
                                results={results}
                                language={language}
                                translations={t}
                                person1FormData={person1}
                                person2FormData={person2}
                            />

                            {/* Call to Action */}
                            <div className="mt-20 p-6 md:p-12 rounded-[32px] md:rounded-[48px] bg-white/[0.08] border border-white/20 text-center relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600/10 blur-[120px] rounded-full -mr-48 -mt-48 transition-colors group-hover:bg-primary-600/20" />
                                <div className="relative z-10">
                                    <h2 className="text-2xl md:text-5xl font-black text-white mb-6">{t.wantGuidance}</h2>
                                    <p className="text-primary-200/60 text-base md:text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                                        {t.guidanceDesc}
                                    </p>
                                    <button
                                        onClick={() => navigate('/dashboard')}
                                        className="px-6 md:px-10 py-4 md:py-5 bg-white text-primary-900 font-black rounded-2xl hover:scale-105 transition-colors shadow-2xl flex items-center gap-3 mx-auto uppercase tracking-widest text-sm md:text-base"
                                    >
                                        <Sparkles size={20} />
                                        {t.chatWithAI}
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </main>

            <Footer />
            <MobileBottomNav />

            {/* Share Modal */}
            {results && (
                <ShareCard
                    isOpen={showShareModal}
                    onClose={() => setShowShareModal(false)}
                    person1Name={results.person1?.name || ''}
                    person2Name={results.person2?.name || ''}
                    totalScore={results.compatibility_data?.guna_milan?.total || 0}
                    maxScore={results.compatibility_data?.guna_milan?.max_total || 36}
                    compatibilityLevel={`${getCompatibilityLevel().label} ${t.compatibility}`}
                    person1Rashi={translateRashi(results.compatibility_data?.charts?.[0]?.rashi, language)}
                    person2Rashi={translateRashi(results.compatibility_data?.charts?.[1]?.rashi, language)}
                    description={results.compatibility_data?.summary?.[language === 'hindi' ? 'hindi' : 'english']?.summary || results.compatibility_data?.summary?.summary || ''}
                    language={language}
                    translations={t}
                />
            )}
        </div>
    );
};

export default FreeCompatibility;
